import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React from "react";
import { Link } from "react-router-dom";
import Modal from "../../src/View/UI/Modal/Modal";
import Loading from "../../src/View/UI/Loading/Loading";
import Checkbox from "../../src/View/UI/Form/Checkbox";
import { SaveButton, CancelButton, } from "../../src/View/UI/StandardButtons/StandardButtons";
import ModalHeader from "../../src/View/UI/Modal/ModalHeader";
import { AlertError, AlertInfo, } from "../../src/View/UI/StandardAlerts/StandardAlerts";
import TabsLayout from "../../src/View/Layout/TabsLayout";
import BasicPager from "../../src/View/UI/BasicPager/BasicPager";
import VirtualizedAppService from "../../src/Services/VirtualizedApp.service";
import useService from "../../src/View/Hooks/useService";
import { reportSourceCreatePath } from "../../src/View/Pages/Report/ReportRoutes";
const PAGE_SIZE = 20;
const WidgetItem = (widgetProps) => {
    return (_jsxs("div", { className: widgetProps.isAdded ? "card-body bg-light" : "card-body", title: widgetProps.isAdded
            ? "Already added to dashboard. To delete go back to edit screen"
            : "", children: [_jsx(Checkbox, { checked: widgetProps.isAdded || widgetProps.isSelected, label: widgetProps.title, onChange: widgetProps.toggleClicked, disabled: widgetProps.isAdded }), widgetProps.description && (_jsx("small", { className: "ml-5", children: widgetProps.description }))] }));
};
const AddWidgetModal = (props) => {
    const [selectedWidgets, setSelectedWidgets] = React.useState([]);
    const [selectedFilters, setSelectedFilters] = React.useState([]);
    const [widgetPageNumber, setWidgetPageNumber] = React.useState(0);
    const [showLink, loadShowLink] = useService(VirtualizedAppService.isPageAccessible);
    React.useEffect(() => {
        loadShowLink("headerReports");
    }, [loadShowLink]);
    const widgetHasNextPage = React.useMemo(() => {
        return props.widgets.length > PAGE_SIZE * (widgetPageNumber + 1);
    }, [widgetPageNumber, props.widgets]);
    const widgetHasPrevPage = React.useMemo(() => {
        return widgetPageNumber > 0;
    }, [widgetPageNumber]);
    const widgetNextPage = () => {
        setWidgetPageNumber((prevIndex) => prevIndex + 1);
    };
    const widgetPrevPage = () => {
        setWidgetPageNumber((prevIndex) => prevIndex - 1);
    };
    const filterHasNextPage = React.useMemo(() => {
        return props.filterHasNext;
    }, [props.filterHasNext]);
    const filterHasPrevPage = React.useMemo(() => {
        return props.filterOffset > 0;
    }, [props.filterOffset]);
    const filterNextPage = () => {
        props.setFilterOffset(props.filterOffset + PAGE_SIZE);
    };
    const filterPrevPage = () => {
        props.setFilterOffset(props.filterOffset - PAGE_SIZE);
    };
    function toggleSelectedArrayItem(item, array, equalsFn, setItemsCallback) {
        const arrayClone = [...array];
        const index = arrayClone.findIndex((i) => equalsFn(item, i));
        if (index >= 0) {
            arrayClone.splice(index, 1);
        }
        else {
            arrayClone.push(item);
        }
        setItemsCallback(arrayClone);
    }
    function toggleSelectWidget(widget) {
        toggleSelectedArrayItem(widget, selectedWidgets, (a, b) => a.id === b.id, setSelectedWidgets);
    }
    function toggleSelectFilter(filter) {
        toggleSelectedArrayItem(filter, selectedFilters, (a, b) => a.id === b.id, setSelectedFilters);
    }
    function isWidgetSelected(widget) {
        return selectedWidgets.findIndex((w) => w.id === widget.id) >= 0;
    }
    function isFilterSelected(filter) {
        return selectedFilters.findIndex((f) => f.id === filter.id) >= 0;
    }
    function isFilterAdded(filter) {
        return props.addedFilters.findIndex((f) => f.id === filter.id) >= 0;
    }
    function isWidgetAdded(widget) {
        return props.addedWidgets.findIndex((w) => w.id === widget.id) >= 0;
    }
    function onSaveClick() {
        if (props.onSave) {
            props.onSave(selectedWidgets, selectedFilters);
        }
        setSelectedFilters([]);
        setSelectedWidgets([]);
    }
    function renderWidget(w) {
        return (_jsx("div", { className: "card mb-3", children: _jsx(WidgetItem, { isSelected: isWidgetSelected(w), toggleClicked: () => toggleSelectWidget(w), title: w.title, description: w.description, isAdded: isWidgetAdded(w) }, w.id) }));
    }
    function renderWidgets() {
        if (!props.widgets) {
            return _jsx("div", {});
        }
        if (!props.widgets.length) {
            return (_jsx(AlertInfo, { message: "There are no more content items left to add." }));
        }
        const offset = widgetPageNumber * PAGE_SIZE;
        return props.widgets.slice(offset, offset + PAGE_SIZE).map(renderWidget);
    }
    function renderFilter(f) {
        return (_jsx("div", { className: "card mb-3", children: _jsx(WidgetItem, { title: f.name, description: f.description, isSelected: isFilterSelected(f), toggleClicked: () => {
                    toggleSelectFilter(f);
                }, isAdded: isFilterAdded(f) }, f.id) }));
    }
    function renderFilters() {
        var _a;
        if (!props.filters) {
            return _jsx("div", {});
        }
        if (!props.filters.length) {
            return (_jsx(AlertInfo, { message: _jsxs("span", { children: ["There are no reports left to add.", " ", ((_a = showLink === null || showLink === void 0 ? void 0 : showLink.data) === null || _a === void 0 ? void 0 : _a.data) && (_jsx(Link, { to: reportSourceCreatePath({}), className: "alert-link", children: "Click here to create a new report" }))] }) }));
        }
        return props.filters.map(renderFilter);
    }
    return (_jsxs(Modal, { isOpen: props.isOpen, onModalClosed: props.onModalClosed, size: "lg", children: [_jsx(ModalHeader, { title: "Add Content" }), _jsxs("div", { className: "modal-body", children: [props.errorMessage && (_jsx(AlertError, { message: "We're sorry, but there was an error loading the widgets.", detail: props.errorMessage })), !props.errorMessage && (_jsx(TabsLayout, { tabs: [
                            {
                                tab: "Standard",
                                content: props.isLoading ? (_jsx(Loading, { isCentered: true, size: "3x" })) : (_jsxs("div", { className: "h-100 mr-3", children: [renderWidgets(), _jsx("div", { className: "mx-2", children: _jsx(BasicPager, { onClickNext: widgetNextPage, onClickPrevious: widgetPrevPage, hasNext: widgetHasNextPage, hasPrevious: widgetHasPrevPage }) })] })),
                                id: "Standard",
                            },
                            {
                                tab: "My Reports",
                                content: props.isLoading ? (_jsx(Loading, { isCentered: true, size: "3x" })) : (_jsxs("div", { className: "h-100 mr-3", children: [renderFilters(), _jsx("div", { className: "mx-2", children: _jsx(BasicPager, { onClickNext: filterNextPage, onClickPrevious: filterPrevPage, hasNext: filterHasNextPage, hasPrevious: filterHasPrevPage }) })] })),
                                id: "MyReport",
                            },
                        ], tabStyle: "underline", children: ({ tabs, content }) => (_jsxs(_Fragment, { children: [tabs, _jsx("div", { className: "w-100 mt-3 custom-scrollbar", style: { height: 500, overflowY: "scroll" }, children: content })] })) }))] }), _jsxs("div", { className: "modal-footer", children: [!props.errorMessage && (_jsx(SaveButton, { className: "btn btn-primary", "data-dismiss": "modal", onClick: () => {
                            onSaveClick();
                        }, disabled: props.isLoading })), _jsx(CancelButton, { className: "btn btn-outline-secondary", "data-dismiss": "modal" })] })] }));
};
export default AddWidgetModal;
